import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { useInventoryVoice } from '../../hooks/useInventoryVoice';
import NeedlMark from '../Brand/NeedlMark';
import { useAssistant } from '../../assistant/AssistantContext';
import { clearAuthSession, getAuthUser } from '../../auth/session';
import { ENV_LABEL, apiUrl } from '../../config';
import { NAV_ITEMS, activeNavKey, assistantPlaceholder, sectionForPath } from '../../navigation';
import { tokens } from '../../theme';

const TOPBAR_HEIGHT = 60;

function useAuthUser() {
  const [user, setUser] = useState(() => getAuthUser());
  const location = useLocation();
  useEffect(() => {
    const refresh = () => setUser(getAuthUser());
    window.addEventListener('storage', refresh);
    window.addEventListener('needl:auth-changed', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('needl:auth-changed', refresh);
    };
  }, []);
  useEffect(() => {
    setUser(getAuthUser());
  }, [location.pathname]);
  return user;
}

function AssistantBar({ authed, variant = 'desktop' }) {
  const { submit, pending } = useAssistant();
  const location = useLocation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [voiceNote, setVoiceNote] = useState('');
  const section = sectionForPath(location.pathname);
  // Dictation: the microphone types into this box and sends it like Enter.
  const voice = useInventoryVoice({
    onTranscript: (transcript) => {
      if (transcript) setText(transcript);
    },
    onResult: ({ transcript }) => {
      const spoken = String(transcript || '').replace(/\s+/g, ' ').trim();
      if (!spoken) {
        setVoiceNote('Nothing heard. Try again.');
        return;
      }
      setText(spoken);
      void submit(spoken);
    },
    onError: (message) => setVoiceNote(String(message || 'Microphone unavailable').slice(0, 80)),
  });
  useEffect(() => {
    if (!voiceNote) return undefined;
    const timer = window.setTimeout(() => setVoiceNote(''), 3500);
    return () => window.clearTimeout(timer);
  }, [voiceNote]);
  const listening = voice.isRecording;
  const thinking = voice.isBusy && !voice.isRecording;
  const placeholder = voiceNote
    ? voiceNote
    : listening
      ? 'Listening… tap the mic when you are done'
      : thinking
        ? 'Working on what you said…'
        : assistantPlaceholder(section, { authed });

  useEffect(() => {
    // Mirror a query carried by the URL so the bar always shows what the page
    // is filtered by; every other route starts empty.
    const q = new URLSearchParams(location.search || '').get('q') || '';
    setText(q);
  }, [location.pathname, location.search]);

  const clear = () => {
    setText('');
    const params = new URLSearchParams(location.search || '');
    if (params.has('q')) {
      params.delete('q');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`);
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ width: '100%', maxWidth: { xs: '100%', md: 760, lg: 920, xl: 1100 }, alignItems: 'center' }}>
    <Box
      component="form"
      role="search"
      data-testid={variant === 'mobile' ? 'header-assistant-mobile' : 'header-assistant'}
      data-section={section}
      onSubmit={(event) => {
        event.preventDefault();
        submit(text);
      }}
      sx={{ flex: 1, minWidth: 0 }}
    >
      <TextField
        fullWidth
        size="small"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder, 'data-testid': 'assistant-input', enterKeyHint: 'search' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: tokens.textMuted }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {pending ? (
                <CircularProgress size={16} thickness={5} sx={{ color: tokens.mint }} data-testid="assistant-pending" />
              ) : text ? (
                <IconButton size="small" aria-label="Clear search" onClick={clear} sx={{ width: 28, height: 28 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: tokens.raised,
            borderRadius: '10px',
            minHeight: 42,
            ...(listening ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,107,122,0.7)' } } : {}),
          },
        }}
      />
    </Box>
    {authed ? (
      <Tooltip title={listening ? 'Stop and send' : thinking ? 'Working' : 'Speak instead of typing'}>
        <span>
          <IconButton
            aria-label={listening ? 'Stop and send what you said' : 'Speak instead of typing'}
            aria-pressed={listening}
            data-testid={variant === 'mobile' ? 'header-microphone-mobile' : 'header-microphone'}
            data-voice-phase={voice.phase}
            disabled={pending || thinking}
            onClick={() => {
              if (listening) {
                voice.stop();
                return;
              }
              setVoiceNote('');
              void voice.start({ dictate: true, sectionId: section, sessionKey: `main:web:${section}:assistant-voice@needl` });
            }}
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: listening ? 'rgba(255,107,122,0.7)' : tokens.line,
              color: listening ? '#fff' : tokens.text,
              bgcolor: listening ? 'rgba(255,107,122,0.85)' : tokens.raised,
              '&:hover': { bgcolor: listening ? 'rgba(255,107,122,0.95)' : 'rgba(255,255,255,0.08)' },
              '&.Mui-disabled': { color: tokens.textFaint, bgcolor: tokens.raised },
            }}
          >
            {thinking ? <CircularProgress size={18} color="inherit" /> : listening ? <StopRoundedIcon /> : <MicNoneOutlinedIcon />}
          </IconButton>
        </span>
      </Tooltip>
    ) : null}
    </Stack>
  );
}

function AccountMenu({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const nextPath = `${location.pathname || '/'}${location.search || ''}`;

  if (!user) {
    return (
      <Button
        component={RouterLink}
        to={`/login?next=${encodeURIComponent(nextPath)}`}
        variant="outlined"
        size="small"
        data-testid="header-signin"
        sx={{ flexShrink: 0, minHeight: 36 }}
      >
        Sign in
      </Button>
    );
  }

  const close = () => setAnchor(null);
  const signOut = () => {
    close();
    clearAuthSession();
    navigate('/');
  };

  return (
    <>
      <Tooltip title={user.name || user.email || 'Account'}>
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          data-testid="header-account"
          sx={{ p: 0.5, flexShrink: 0 }}
        >
          <Avatar src={user.picture || ''} alt={user.name || user.email || 'User'} sx={{ width: 34, height: 34, fontSize: 14, bgcolor: tokens.raised }}>
            {String(user.name || user.email || 'U').trim().charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': 'Account', dense: true, sx: { minWidth: 220 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{user.name || 'Signed in'}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{user.email || ''}</Typography>
        </Box>
        <Divider />
        <MenuItem component={RouterLink} to="/profile" onClick={close} data-testid="header-menu-profile">
          <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem component={RouterLink} to="/orders" onClick={close} data-testid="header-menu-orders">
          <ListItemIcon><LocalShippingOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Orders</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={signOut} data-testid="header-logout">
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

function NavButton({ item, active, compact = false, prominent = false }) {
  const Icon = item.icon;
  return (
    <ButtonBase
      component={RouterLink}
      to={item.path}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      data-testid={compact ? `bottom-nav-${item.key}` : `side-nav-${item.key}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: compact ? 0.35 : 0.6,
        minHeight: compact ? 52 : 62,
        flex: compact ? 1 : '0 0 auto',
        mx: compact ? 0 : 1,
        borderRadius: '10px',
        color: active ? tokens.mint : tokens.textMuted,
        bgcolor: active && !compact ? tokens.mintSoft : 'transparent',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': { color: tokens.text, bgcolor: compact ? 'transparent' : 'rgba(255,255,255,0.05)' },
        '&.Mui-focusVisible': { outline: `2px solid ${tokens.mint}`, outlineOffset: -2 },
      }}
    >
      {prominent ? (
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '50%',
            bgcolor: tokens.mint,
            color: tokens.onMint,
            mt: '-14px',
            border: `4px solid ${tokens.bg}`,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
      ) : (
        <Icon sx={{ fontSize: compact ? 22 : 23 }} />
      )}
      <Typography
        component="span"
        data-testid={compact ? undefined : `side-nav-${item.key}-label`}
        sx={{ fontSize: compact ? '0.64rem' : '0.7rem', fontWeight: 600, lineHeight: 1, letterSpacing: '0.01em' }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  );
}

export function SideRail() {
  const location = useLocation();
  const active = activeNavKey(location.pathname);
  return (
    <Box
      component="nav"
      aria-label="Primary"
      data-testid="desktop-side-nav"
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'fixed',
        top: TOPBAR_HEIGHT,
        bottom: 0,
        left: 0,
        width: 'var(--needl-rail-width)',
        flexDirection: 'column',
        gap: 0.5,
        pt: 1.5,
        bgcolor: tokens.bg,
        borderRight: `1px solid ${tokens.line}`,
        zIndex: (theme) => theme.zIndex.appBar - 1,
      }}
    >
      {NAV_ITEMS.map((item) => (
        <NavButton key={item.key} item={item} active={active === item.key} />
      ))}
    </Box>
  );
}

export function BottomNav() {
  const location = useLocation();
  const active = activeNavKey(location.pathname);
  return (
    <Box
      component="nav"
      aria-label="Primary"
      data-testid="bottom-nav"
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: 'calc(var(--needl-bottom-nav-height) + env(safe-area-inset-bottom, 0px))',
        pb: 'env(safe-area-inset-bottom, 0px)',
        px: 0.5,
        bgcolor: tokens.bg,
        borderTop: `1px solid ${tokens.line}`,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      {NAV_ITEMS.filter((item) => item.mobile).map((item) => (
        <NavButton key={item.key} item={item} active={active === item.key} compact prominent={item.key === 'capture'} />
      ))}
    </Box>
  );
}

export default function Header() {
  const user = useAuthUser();
  const [status, setStatus] = useState({ e2e: false });

  useEffect(() => {
    let cancelled = false;
    if (typeof fetch !== 'function') return undefined;
    fetch(apiUrl('/api/status'))
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!cancelled && data && typeof data === 'object') setStatus({ e2e: Boolean(data.e2e) });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: tokens.bg,
          color: tokens.text,
          borderBottom: `1px solid ${tokens.line}`,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Toolbar
          disableGutters
          data-testid="header-layout"
          sx={{
            minHeight: { xs: TOPBAR_HEIGHT, md: TOPBAR_HEIGHT },
            px: { xs: 2, md: 3 },
            gap: { xs: 1.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            component={RouterLink}
            to={user ? '/market' : '/'}
            data-testid="header-brand"
            aria-label="needL home"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              textDecoration: 'none',
              color: 'inherit',
              flexShrink: 0,
              width: { md: 'calc(var(--needl-rail-width) - 24px)' },
              minWidth: { md: 120 },
            }}
          >
            <NeedlMark sx={{ fontSize: 30, color: tokens.mint }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.01em' }}>needL</Typography>
            {ENV_LABEL ? (
              <Chip label={ENV_LABEL} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.64rem', color: tokens.warn, borderColor: 'rgba(255,194,102,0.5)' }} />
            ) : null}
            {status.e2e ? (
              <Tooltip title="E2E stubs are enabled. Restart without docker-compose.e2e.yml to use real generation.">
                <Chip label="E2E" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.64rem', color: tokens.danger, borderColor: 'rgba(255,107,122,0.5)' }} />
              </Tooltip>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <AssistantBar authed={Boolean(user)} />
          </Box>
          <Box sx={{ flex: 1, display: { xs: 'block', md: 'none' } }} />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <AccountMenu user={user} />
          </Stack>
        </Toolbar>
        <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, pb: 1.25 }}>
          <AssistantBar authed={Boolean(user)} variant="mobile" />
        </Box>
      </AppBar>
      <SideRail />
      <BottomNav />
    </>
  );
}
